import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { SESSION_MONITORING_CONFIG } from './session-monitoring-config.token';
import { SessionMonitoringConfig } from './session-monitoring.config';
import { BehaviorSubject, EMPTY, fromEvent, merge, Observable, Subject, Subscription, throttleTime, tap } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { WorkerBroker } from './worker-broker';

@Injectable({
  providedIn: 'root'
})
export class SessionMonitoringService implements OnDestroy {

  /**
   * Emits when the user becomes idle
   */
  onIdleStart = new Subject<void>();

  /**
   * Emits every second when the user is idle
   */
  onTimeoutWarning = new BehaviorSubject<number>(0);

  /**
   * Emits when the user times out
   */
  onTimeout = new Subject<void>();

  /**
   * Emits when there is an interrupt on inactivity
   */
  onInterrupt = new Subject<void>();

  /**
  * Emits when the user token has been refreshed successfully
  */
  private _onTokenRefreshed = new Subject<void>();
  onTokenRefreshed = this._onTokenRefreshed.asObservable();


  private _running = false;
  private _idling = false;

  private _documentInterruptEvents = ['mousedown','keydown','wheel','scroll','touchstart','touchmove'];

  private _expiresOn = -1;

  private _idleInterval: number | null = null;
  private _timeoutInterval: number | null = null;
  private _keepAliveInterval: number | null = null;

  private _interruptSubscription = Subscription.EMPTY;

  private broker: WorkerBroker;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(SESSION_MONITORING_CONFIG) private readonly config: SessionMonitoringConfig,
    private readonly zone: NgZone,
    private readonly http: HttpClient
  ) {
    if (this.config.refreshUrl === null || typeof this.config.refreshUrl === 'undefined') {
      throw new Error(`No 'refreshUrl' provided in SessionMonitoringConfig. Please provide an endpoint.`);
    }

    this.broker = new WorkerBroker(config.workerUrl);

    this.zone.runOutsideAngular(() => {
      this.broker.initWorker();
    });
  }

  /**
   * Start watching inactivity
   */
  watch(watchInterrupts = true, watchKeepAlive = true): void {
    this.safeClearInterval('_idleInterval');
    this.safeClearInterval('_timeoutInterval');

    this._running = true;

    this._expiresOn = new Date().getTime() + (this.config.idleTime + this.config.idleWarningTime) * 1000;

    this.log(`Idle in ${this.secondsUntilIdle()} seconds`);

    this.setIdleInterval();

    if (watchInterrupts) {
      this.watchInterrupts();
    }

    if (watchKeepAlive) {
      this.safeClearInterval('_keepAliveInterval');
      this.watchKeepAlive();
    }
  }

  /**
   * Stop watching inactivity
   */
  stop(): void {
    this.safeClearInterval('_idleInterval');
    this.safeClearInterval('_timeoutInterval');
    this.safeClearInterval('_keepAliveInterval');

    this._running = false;
    this._idling = false;
    this._expiresOn = -1;

    this.stopInterrupts();
  }

  /**
   * Start watching interrupt events
   */
  watchInterrupts(): void {
    this.stopInterrupts();

    const target = this.document.documentElement;
    const options = { passive: false };
    const fromEvents = this._documentInterruptEvents.map(event => fromEvent(target, event, options));

    const eventSrc = merge(...fromEvents).pipe(throttleTime(500));

    this.zone.runOutsideAngular(() => {
      this._interruptSubscription = eventSrc.subscribe(() => {
        this.interrupt();
      });
    });
  }

  watchKeepAlive(): void {
    this.setKeepAliveInterval();
  }

  /**
   * Stop watching all interrupts events
   */
  stopInterrupts(): void {
    this._interruptSubscription.unsubscribe();
  }

  /**
   * Trigger an idle state
   */
  idle(): void {
    this.log('User idle');

    this.safeClearInterval('_idleInterval');
    this.safeClearInterval('_keepAliveInterval');

    this.stopInterrupts();

    this._idling = true;

    this.onIdleStart.next();

    this.log(`Timeout in ${this.config.idleWarningTime} seconds`);
    this.onTimeoutWarning.next(this.config.idleWarningTime);

    this.setTimoutInterval();
  }

  /**
   * Triggers a timeout
   */
  timeout(): void {
    this.log('User timeout');

    this.safeClearInterval('_idleInterval');
    this.safeClearInterval('_timeoutInterval');
    this.safeClearInterval('_keepAliveInterval');

    this.onTimeout.next();
  }

  /**
   * Interrupt inactivity
   */
  interrupt(): void {
    if (!this._idling) {
      this.log('Interrupt triggered');

      this.onInterrupt.next();

      this.watch(false, false);
    }
  }

  refresh(): Observable<void> {
    if (this.config.refreshUrl) {
      return this.http.get<void>(this.config.refreshUrl).pipe(
        tap(() => this.zone.run(() => this._onTokenRefreshed.next()))
      );
    }
    return EMPTY;
  }

  ngOnDestroy() {
    this.stop();
    this._onTokenRefreshed.complete();
  }

  private safeClearInterval(intervalProp: '_idleInterval' | '_timeoutInterval' | '_keepAliveInterval') {
    const interval: number | null = this[intervalProp];
    if (interval !== null && typeof interval !== 'undefined') {
      this.broker.clearInterval(interval);
      this[intervalProp] = null;
    }
  }

  private setIdleInterval(): void {
    this.safeClearInterval('_idleInterval');

    this.zone.runOutsideAngular(() => {
      this._idleInterval = this.broker.setInterval(() => {
        const secondsUntilIdle = this.secondsUntilIdle();

        this.log(`Idle in ${secondsUntilIdle} seconds`);

        if (secondsUntilIdle <= 0) {
          this.zone.run(() => {
              this.idle();
          });
        }
      }, 1000);
    });
  }

  private setTimoutInterval(): void {
    this.zone.runOutsideAngular(() => {
      this._timeoutInterval = this.broker.setInterval(() => {
        const secondsUntilTimeout = this.secondsUntilTimeout();

        this.log(`Timeout in ${secondsUntilTimeout} seconds`);

        this.zone.run(() => {
          this.onTimeoutWarning.next(secondsUntilTimeout);

          if (secondsUntilTimeout <= 0) {
            this.timeout();
            return;
          }
        });
      }, 1000)
    });
  }

  /*
   * Refreshes on every tick, regardless of recent activity. Gating the refresh on activity would
   * let the token expire while the session still counts as active: the idle countdown (idleTime)
   * runs far longer than the token lifetime, so a user who reads or watches the screen without
   * clicking would keep an "active" session carrying a dead token. Walking away is handled by
   * idle()/timeout(), which clear this interval.
   */
  private setKeepAliveInterval(): void {
    this.zone.runOutsideAngular(() => {
      this._keepAliveInterval = this.broker.setInterval(() => {
        this.refresh().subscribe({
          error: (err: Error) => {
            this.log(`Token refresh failed: ${err}`);
          }
        });
      }, this.config.keepAliveTime * 1000);
    });
  }

  private secondsUntilIdle(): number {
    return Math.round((this._expiresOn - this.config.idleWarningTime * 1000 - new Date().getTime()) / 1000);
  }

  private secondsUntilTimeout(): number {
    return Math.round((this._expiresOn - new Date().getTime()) / 1000);
  }

  private log(message: string): void {
    if (!this.config.prodMode) {
      console.log(`[SessionMonitor] ${message}`);
    }
  }
}
